// Stable, readable URLs for projects (/projects/<slug>), derived from the data.
// A project can pin its own `slug` in projectsData.js (do that if you rename a
// project, so old links keep working). Otherwise it comes from the name.
// Duplicates get -2, -3, ... so every project always has a unique address.
import { projectsData } from '../data/projectsData';

export function slugify(text) {
    return String(text || '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

// [{ project, slug }] in the same order as the data.
export function buildProjectIndex(projects = projectsData) {
    const used = new Set();
    return projects.map((project) => {
        const base = slugify(project.slug || project.projectName) || `project-${project.id}`;
        let slug = base;
        for (let n = 2; used.has(slug); n += 1) slug = `${base}-${n}`;
        used.add(slug);
        return { project, slug };
    });
}

const index = buildProjectIndex();

export const projectPath = (slug) => `/projects/${slug}`;
export const slugForId = (id) => (index.find((entry) => entry.project.id === id) || {}).slug;
export const findBySlug = (slug) => index.find((entry) => entry.slug === slug) || null;
export const allSlugs = () => index.map((entry) => entry.slug);

// Neighbours in list order, and other projects sharing the most technologies.
export function neighbours(slug) {
    const at = index.findIndex((entry) => entry.slug === slug);
    if (at < 0) return { previous: null, next: null };
    return { previous: index[at - 1] || null, next: index[at + 1] || null };
}

export function related(slug, limit = 3) {
    const current = findBySlug(slug);
    if (!current) return [];
    const tags = new Set(current.project.tags || []);
    return index
        .filter((entry) => entry.slug !== slug)
        .map((entry) => ({ entry, shared: (entry.project.tags || []).filter((tag) => tags.has(tag)).length }))
        .filter(({ shared }) => shared > 0)
        .sort((a, b) => b.shared - a.shared)
        .slice(0, limit)
        .map(({ entry }) => entry);
}
